/**
 * Descriptions and examples for each component type
 * Used in the component form to help users understand what each type represents
 */

export interface ComponentTypeInfo {
  examples: string;
}

export const COMPONENT_TYPE_DESCRIPTIONS: Record<string, ComponentTypeInfo> = {
  // Applications
  application: {
    examples: 'Web apps, mobile apps, desktop apps',
  },
  api: {
    examples: 'REST API, GraphQL, gRPC',
  },
  service: {
    examples: 'Microservices, backend services',
  },
  function: {
    examples: 'Azure Functions, AWS Lambda',
  },
  saas: {
    examples: 'Microsoft 365, Salesforce, ServiceNow',
  },

  // Data Storage
  database: {
    examples: 'PostgreSQL, SQL Server, MongoDB',
  },
  storage: {
    examples: 'Azure Blob, S3, file shares',
  },
  cache: {
    examples: 'Redis, Memcached',
  },
  queue: {
    examples: 'Azure Service Bus, RabbitMQ',
  },
  data_lake: {
    examples: 'Azure Data Lake, S3 data lake',
  },
  message_broker: {
    examples: 'Kafka, Azure Event Hubs',
  },

  // Infrastructure
  server: {
    examples: 'Physical servers, bare metal',
  },
  virtual_machine: {
    examples: 'Azure VM, EC2, VMware',
  },
  container: {
    examples: 'Docker containers',
  },
  container_orchestrator: {
    examples: 'Kubernetes, AKS, EKS, OpenShift',
  },
  container_registry: {
    examples: 'ACR, Docker Hub, Harbor',
  },
  cluster: {
    examples: 'Server clusters, compute clusters',
  },
  load_balancer: {
    examples: 'Azure LB, AWS ALB, F5, HAProxy',
  },
  cdn: {
    examples: 'Azure CDN, CloudFront, Akamai',
  },

  // Network
  network: {
    examples: 'VNet, subnet, VLAN',
  },
  firewall: {
    examples: 'Azure Firewall, Palo Alto, FortiGate',
  },
  proxy: {
    examples: 'Nginx, reverse proxy, forward proxy',
  },
  gateway: {
    examples: 'API Gateway, App Gateway',
  },
  vpn: {
    examples: 'Site-to-site VPN, P2S VPN',
  },
  dns: {
    examples: 'Azure DNS, Route53, bind',
  },

  // Security
  identity_provider: {
    examples: 'Entra ID, Okta, Auth0',
  },
  secret_manager: {
    examples: 'Azure Key Vault, HashiCorp Vault',
  },
  security_zone: {
    examples: 'DMZ, restricted zone, high-security zone',
  },

  // Zones
  zone: {
    examples: 'Network zone, trust zone, environment zone',
  },
  cloud_platform: {
    examples: 'Azure, AWS, GCP',
  },

  // Platform
  platform: {
    examples: 'Shared platform, application platform',
  },
  integration_platform: {
    examples: 'MuleSoft, Azure Logic Apps',
  },

  // CI/CD
  cicd_pipeline: {
    examples: 'Azure DevOps, GitHub Actions, Jenkins',
  },
  gitops: {
    examples: 'ArgoCD, Flux, GitLab GitOps, Jenkins X',
  },

  // Monitoring
  monitoring_service: {
    examples: 'Azure Monitor, Datadog, Prometheus',
  },
  logging_service: {
    examples: 'Log Analytics, ELK Stack, Splunk',
  },
  backup_service: {
    examples: 'Azure Backup, Veeam, Commvault',
  },

  // Other
  other: {
    examples: 'Custom or specialized components',
  },
};

/**
 * Get description for a component type
 */
export const getComponentTypeDescription = (
  type: string
): ComponentTypeInfo | undefined => {
  return COMPONENT_TYPE_DESCRIPTIONS[type];
};
